import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import {
  useGetAreasQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
} from "./areasApi";

// New master screen (added to the API 2026-08-12) - structural clone of
// DepartmentsPage.jsx, same global/shared master pattern. Reading requires
// Area.View (route-gated, see AppRoutes.jsx), writes gated by
// Area.Create/.Edit + an all-branch caller (§13.3's usual rule for global
// masters). List starts empty until areas are added here - the legacy
// area_master rows aren't surfaced by this endpoint at all.
const AreasPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Area");
  const { data: records = [], isLoading } = useGetAreasQuery();
  const [createArea, { isLoading: isCreating }] = useCreateAreaMutation();
  const [updateArea, { isLoading: isUpdating }] = useUpdateAreaMutation();
  const [deleteArea] = useDeleteAreaMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState("");

  const selectedRecord = records.find((r) => r.areaId === selectedId) || null;

  useEffect(() => {
    setName(mode === "new" ? "" : selectedRecord?.areaName || "");
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (id) => {
    setSelectedId(id);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedId(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (mode === "edit" && selectedId != null) {
        await updateArea({ areaId: selectedId, areaName: name.trim() }).unwrap();
      } else {
        await createArea({ areaName: name.trim() }).unwrap();
      }
      toast.success("Area saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save area");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.areaName}?` });
    if (!confirmed) return;
    try {
      await deleteArea(selectedRecord.areaId).unwrap();
      toast.success("Area deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete area");
    }
  };

  const columns = [
    { title: "Area", dataIndex: "areaName" },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.areaId)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Areas</h4>
              <h6>Area master data</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing areas requires Area.Create/.Edit and an
            all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Id</label>
                <input
                  type="text"
                  className="form-control"
                  value={mode === "new" ? "Auto-assigned" : selectedId ?? ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-7 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={records}
                rowKey="areaId"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreasPage;
